import axios from "axios";
import { API_URL, getAuthHeader } from ".";
import {
  PartnerOpportunity,
  PartnerOpportunityMemberRegionPreferences,
  PartnerOpportunityMemberStatePreferences,
  PartnerOpportunityManufacturerMember,
  PartnerOpportunityMetadata,
  PartnerOpportunityModerateRequest,
  PartnerOpportunityMyResponseDto,
  PartnerOpportunityRegionOption,
  PartnerOpportunityResponseDto,
  PartnerOpportunitySavedFilter,
  PartnerOpportunitySearchRequest,
} from "../Models/PartnerOpportunity";
import { ResponseModel } from "../Models/ResponseModel";

class PartnerOpportunitiesApi {
  static getMetadata() {
    return axios.get<PartnerOpportunityMetadata>(
      `${API_URL}/api/partneropportunities/metadata`,
      getAuthHeader()
    );
  }

  static getManufacturerMembers() {
    return axios.get<PartnerOpportunityManufacturerMember[]>(
      `${API_URL}/api/partneropportunities/manufacturer-members`,
      getAuthHeader()
    );
  }

  static getRegions() {
    return axios.get<PartnerOpportunityRegionOption[]>(
      `${API_URL}/api/partneropportunities/regions`,
      getAuthHeader()
    );
  }

  static upsertRegion(request: PartnerOpportunityRegionOption) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/regions`,
      request,
      getAuthHeader()
    );
  }

  static deleteRegion(id: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/partneropportunities/regions/${id}`,
      getAuthHeader()
    );
  }

  static getMemberRegionPreferences(memberId: number) {
    return axios.get<PartnerOpportunityMemberRegionPreferences>(
      `${API_URL}/api/partneropportunities/members/${memberId}/region-preferences`,
      getAuthHeader()
    );
  }

  static saveMemberRegionPreferences(request: PartnerOpportunityMemberRegionPreferences) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/members/${request.memberId}/region-preferences`,
      request,
      getAuthHeader()
    );
  }

  static getMemberStatePreferences(memberId: number) {
    return axios.get<PartnerOpportunityMemberStatePreferences>(
      `${API_URL}/api/partneropportunities/members/${memberId}/state-preferences`,
      getAuthHeader()
    );
  }

  static saveMemberStatePreferences(request: PartnerOpportunityMemberStatePreferences) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/members/${request.memberId}/state-preferences`,
      request,
      getAuthHeader()
    );
  }

  static search(request: PartnerOpportunitySearchRequest) {
    return axios.post<PartnerOpportunity[]>(
      `${API_URL}/api/partneropportunities/search`,
      request,
      getAuthHeader()
    );
  }

  static getMy() {
    return axios.get<PartnerOpportunity[]>(
      `${API_URL}/api/partneropportunities/my`,
      getAuthHeader()
    );
  }

  static getByMember(memberId: number) {
    return axios.get<PartnerOpportunity[]>(
      `${API_URL}/api/partneropportunities/member/${memberId}`,
      getAuthHeader()
    );
  }

  static getById(id: number) {
    return axios.get<PartnerOpportunity>(`${API_URL}/api/partneropportunities/${id}`, getAuthHeader());
  }

  static getResponses(opportunityId: number) {
    return axios.get<PartnerOpportunityResponseDto[]>(
      `${API_URL}/api/partneropportunities/${opportunityId}/responses`,
      getAuthHeader()
    );
  }

  static addResponse(opportunityId: number, message: string) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/${opportunityId}/responses`,
      { message },
      getAuthHeader()
    );
  }

  static getMyResponses() {
    return axios.get<PartnerOpportunityMyResponseDto[]>(
      `${API_URL}/api/partneropportunities/responses/mine`,
      getAuthHeader()
    );
  }

  static upsert(request: PartnerOpportunity) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/upsert`,
      request,
      getAuthHeader()
    );
  }

  static submit(id: number) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}/submit`,
      {},
      getAuthHeader()
    );
  }

  static memberLifecycle(id: number, status: "Published" | "Archived") {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}/lifecycle`,
      { status },
      getAuthHeader()
    );
  }

  static moderate(id: number, request: PartnerOpportunityModerateRequest) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}/moderate`,
      request,
      getAuthHeader()
    );
  }

  static delete(id: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}`,
      getAuthHeader()
    );
  }

  static favorite(id: number) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}/favorite`,
      {},
      getAuthHeader()
    );
  }

  static unfavorite(id: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/partneropportunities/${id}/favorite`,
      getAuthHeader()
    );
  }

  static getSavedFilters() {
    return axios.get<PartnerOpportunitySavedFilter[]>(
      `${API_URL}/api/partneropportunities/saved-filters`,
      getAuthHeader()
    );
  }

  static upsertSavedFilter(request: PartnerOpportunitySavedFilter) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/partneropportunities/saved-filters`,
      request,
      getAuthHeader()
    );
  }
}

export default PartnerOpportunitiesApi;
